import { makeAutoObservable, runInAction } from 'mobx';
import ErrorBoundaryStore from '../error-boundary-store/error-boundary-store';
import { TGraphQLSubscriptions } from '../../api/graphql-api-client/graphql-api-client';
import { QandaTabs } from '../../constans/question-enums';
import ErrorParsingService, { IErrorObject, IGraphQLErrorResponse } from '../../utils/services/error-parsing-service';
import { RESOURCE_NOT_FOUND_CODE } from '../../constans/error-common-constants';
import { getErrorBoundaryStore } from '../error-boundary-store/error-boundary-store-instanse';
import { authService } from '../../utils/services/auth-service';
import { IDecodedJwtToken } from '../auth-store/auth-store';
import {
  getPresenterInfoApi,
  getPresenterReviewModeApi,
  createPresenterReviewModeApi,
  onUpdatePresenterReviewModeSubscriptionApi,
  updateReviewModeApi,
  IPresentationInfo,
  getPreviewInfo
} from '../../api/dao/podium-info-dao';

export interface IInterfaceSetting {
  presentationId: string;
  reviewMode: boolean;
}

export interface IWindowResolutionInfo {
  height: number;
  width: number;
}

class PodiumInfoStore {
  public presentationInfo: IPresentationInfo;
  public isReviewMode: boolean;
  public isShowReviewModePopUp: boolean;
  public isConfirmationRequired: boolean;
  public tmpReviewModeState: boolean;
  public selectedTab: QandaTabs;
  public isLoading: boolean;
  public isGetPresentationInfoSuccess: boolean;
  public isShowLaunchPreviewModal: boolean;
  private errorParser: ErrorParsingService;
  private presentationId: string | null;
  private windowResolutionInfo: IWindowResolutionInfo;
  private previewLink: string;

  constructor (
    presenterEmbedId: string,
    private readonly errorBoundary: ErrorBoundaryStore = getErrorBoundaryStore()
  ) {
    makeAutoObservable(this);
    this.isLoading = true;
    this.isGetPresentationInfoSuccess = false;
    this.selectedTab = QandaTabs.UNANSWERED;
    this.isReviewMode = false;
    this.isShowReviewModePopUp = false;
    this.isConfirmationRequired = false;
    this.tmpReviewModeState = false;
    this.presentationInfo = {
      presentationName: '',
      interfaceBrandingLogoUrl: '',
    };
    this.errorParser = new ErrorParsingService();
    this.presentationId = authService.decodeJwt<IDecodedJwtToken>(authService.getJwtToken(presenterEmbedId) || '')?.presentationId.toString() || null;
    this.setWindowResolutionInfo();
    this.previewLink = '';
  }

  public getPresentationInfo = async (hashId: string | null): Promise<boolean | void> => {
    if (!hashId) return;
    this.isLoading = true;
    try {
      this.presentationInfo = await getPresenterInfoApi(hashId);
      this.isGetPresentationInfoSuccess = true;
      return true;
    } catch (error) {
      if (this.errorParser.parseApiErrorCode(error as IErrorObject) === RESOURCE_NOT_FOUND_CODE) return false;
      this.isGetPresentationInfoSuccess = false;
      this.errorBoundary.setErrorMessage(error as IErrorObject);
    } finally {
      this.isLoading = false;
    }
  };

  public getPresenterReviewMode = async (): Promise<void> => {
    if (!this.presentationId) return;
    try {
      const result = await getPresenterReviewModeApi(this.presentationId);
      // TODO Need fix graphql response type
      // @ts-ignore
      const { data: { getInterfaceSettings }} = result;
      this.isReviewMode = !!getInterfaceSettings?.reviewMode;
      if (!getInterfaceSettings) {
        this.createPresenterReviewMode();
      }
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public isResolutionBreakPointActive = (breakPoint: number): boolean => {
    return this.windowResolutionInfo.width < breakPoint;
  };

  public get windowResolution (): IWindowResolutionInfo {
    return this.windowResolutionInfo;
  }

  public toggleReviewMode = (): void => {
    this.isShowReviewModePopUp = !this.isShowReviewModePopUp;
  };

  public confirmReviewModeChange = (): void => {
    this.updateReviewMode(!this.isReviewMode);
    this.isReviewMode = !this.isReviewMode;
  };

  public applyReviewModeChanges = (cancelToggle?: boolean): void => {
    this.isReviewMode = this.tmpReviewModeState;
    this.isConfirmationRequired = false;

    if (cancelToggle) {
      this.isShowReviewModePopUp = !cancelToggle;
    } else {
      this.toggleReviewMode();
    }
  };

  public createPresenterReviewMode = async (): Promise<void> => {
    if (!this.presentationId) return;
    const setting: IInterfaceSetting = { presentationId: this.presentationId, reviewMode: false };
    try {
      await createPresenterReviewModeApi({ ...setting });
    } catch (error) {
      this.errorBoundary.setErrorMessage(error as IErrorObject);
    }
  };

  public onUpdatePresenterReviewModeSubscription = (): TGraphQLSubscriptions => {
    return onUpdatePresenterReviewModeSubscriptionApi(
      this.presentationId,
      this.onUpdatePresenterReviewModeCallback,
      () => this.onUpdatePresenterReviewModeSubscription()
    );
  };

  private onUpdatePresenterReviewModeCallback = (data: { onUpdateInterfaceSettings: IInterfaceSetting }): void => {
    const { onUpdateInterfaceSettings: { reviewMode }} = data;
    this.tmpReviewModeState = reviewMode;

    if (this.isShowReviewModePopUp && this.isReviewMode !== reviewMode) {
      this.isConfirmationRequired = true;
    }

    if (this.selectedTab === QandaTabs.UNANSWERED && this.isReviewMode !== reviewMode) {
      this.toggleReviewMode();
      this.isConfirmationRequired = true;
    } else {
      this.isReviewMode = reviewMode;
    }
  };

  public updateSelectedTab = (selectedTab: QandaTabs): void => {
    this.selectedTab = selectedTab;
  };

  private updateReviewMode = async (reviewMode: boolean): Promise<void> => {
    if (!this.presentationId) return;
    const setting: IInterfaceSetting = { presentationId: this.presentationId, reviewMode: reviewMode };
    try {
      await updateReviewModeApi({ ...setting });
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public getPresentationId = (): string | null => {
    return this.presentationId;
  };

  public getPreviewLink = (): string | null => {
    return this.previewLink;
  };

  public getIsShowLaunchPreviewModal = (): boolean => {
    return this.isShowLaunchPreviewModal;
  };

  public setIsShowLaunchPreviewModal = (value: boolean): void => {
    this.isShowLaunchPreviewModal = value;
  };

  public setPresentationId = (presentationId: string): void => {
    this.presentationId = presentationId;
  };

  public setWindowResolutionInfo = (): void => {
    this.windowResolutionInfo = {
      width: window.innerWidth,
      height: window.innerHeight
    };
  };

  public getPreviewInformation = async (): Promise<void> => {
    if (!this.presentationId) return;
    try {
      const data = await getPreviewInfo(this.presentationId);
      runInAction(() => {
        this.previewLink = data.presentationLink;
        this.isShowLaunchPreviewModal = true;
      });
    } catch (error) {
      this.errorBoundary.setErrorMessage(error as IErrorObject);
    }
  };
}

export type TPodiumInfoStore = PodiumInfoStore;

export default PodiumInfoStore;
